import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Paper, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  ProfileSubscriptionRequestViewModel,
  UnsubscribeGroupEnum,
  UnsubscribeGroupsResponseViewModel,
} from '@rsmus/ecp-communicationservice'
import { CloseIcon, PenIcon } from '@rsmus/myrsm-icons'
import { setContactPreferenceFormState } from '../../store/profile/profilePageSlice'
import { Styles } from '../../types'
import SectionHeader from '../layouts/SectionHeader'
import { RootState } from '../../store'
import ContactPreferenceForm from './Preferences/FormControls/ContactPreferenceForm'
import ContactPreferenceView from './Preferences/ContactPreferenceView'
import EmailNotificationPreferences from '../Unsubscribe/EmailNotificationPreferences'
import CustomErrorAlert from '../forms/Alert/CustomErrorAlert/CustomErrorAlert'
import api from '../../api'
import {
  getPaymentRemindersSubscription,
  getPaymentUpdatesSubscription,
  setPaymentRemindersSubscription,
  setPaymentUpdatesSubscription,
  setSubscriptionErrors,
} from '../../store/unsubscribe/emailSubscriptionSlice'
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag'

const styles: Styles = {
  contactSection: (theme) => ({
    gridColumn: 'col 1 / span 9',
    gridRow: 'row 1 / span 1',
    [theme.breakpoints.only('desktop')]: {
      marginRight: '1rem',
    },
    [theme.breakpoints.only('mobile')]: {
      gridColumn: 'col 1 / span 12',
    },
  }),
  modeButtonContainer: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'right',
  },
  contactPreferencesContainer: {
    flexGrow: 1,
  },
  button: {
    paddingX: 0,
    minWidth: 'unset',
    '& svg': { width: '1.5rem', height: '1.5rem' },
  },
}

const PreferencesContainer = () => {
  let abortController: AbortController | null = null
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const paymentRemindersSubscription = useSelector(
    getPaymentRemindersSubscription,
  )
  const paymentUpdatesSubscription = useSelector(getPaymentUpdatesSubscription)
  const [error, setError] = useState<boolean>(false)
  const isViewOnlyMode = useSelector(
    (state: RootState) => state.profilePage.FormValues.isViewOnlyMode,
  )
  const isEmailUnsubscribeFeatureEnabled = useFeatureFlag('EmailUnsubscribe')

  const closeIconClicked = () => {
    dispatch(
      setContactPreferenceFormState({
        isViewOnlyMode: true,
        isSubmissionSuccessful: undefined,
        isSubmissionFailed: undefined,
      }),
    )
  }

  const pencilIconClicked = () => {
    dispatch(
      setContactPreferenceFormState({
        isViewOnlyMode: false,
        isSubmissionSuccessful: undefined,
        isSubmissionFailed: undefined,
      }),
    )
  }

  const sectionHeaderStyleOverride = (theme: Theme) => ({
    display: 'flex',
    marginBottom: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    '& hr': {
      flexShrink: 1,
      alignSelf: 'flex-end',
      marginBottom: '0.125rem',
      marginLeft: '0.5rem',
      borderColor: theme.palette.primary.main,
      width: '100%',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: undefined,
    },
  })

  const fetchUnsubscribeGroups = useMemo(
    () =>
      async (
        callback: (
          results?: UnsubscribeGroupsResponseViewModel | undefined,
        ) => void,
      ) => {
        // Cancel the previous request before making a new request
        if (abortController) {
          abortController.abort('New request initiated')
        }
        abortController = new AbortController()
        const abortSignal = abortController.signal
        await api.communication
          .unsubscribe_ProfileGetUnsubscribeGroupsByEmail(abortSignal)
          .then((response) => {
            callback(response?.data)
          })
          .catch(() => {
            setError(true)
            callback(undefined)
          })
      },
    [],
  )

  const fetchUnsubscribe = async (
    request: { input: UnsubscribeGroupEnum },
    callback: (results?: boolean) => void,
  ) => {
    const requestModel: ProfileSubscriptionRequestViewModel = {
      groupId: request.input,
    }
    // Cancel the previous request before making a new request
    if (abortController) {
      abortController.abort('New request initiated')
    }
    abortController = new AbortController()
    const abortSignal = abortController.signal
    await api.communication
      .unsubscribe_ProfileUnsubscribeEmail(requestModel, abortSignal)
      .then((response) => {
        callback(response?.data)
      })
      .catch(() => {
        dispatch(setSubscriptionErrors([request.input]))
        setError(true)
      })
      .finally(() => {
        // Reset the abort controller after the request is complete
        abortController = null
      })
  }

  const fetchSubscribe = async (
    request: { input: UnsubscribeGroupEnum },
    callback: (results?: boolean) => void,
  ) => {
    const requestModel: ProfileSubscriptionRequestViewModel = {
      groupId: request.input,
    }
    // Cancel the previous request before making a new request
    if (abortController) {
      abortController.abort('New request initiated')
    }
    abortController = new AbortController()
    const abortSignal = abortController.signal
    await api.communication
      .unsubscribe_ProfileSubscribeEmail(requestModel, abortSignal)
      .then((response) => {
        callback(response?.data)
      })
      .catch(() => {
        dispatch(setSubscriptionErrors([request.input]))
        setError(true)
      })
      .finally(() => {
        // Reset the abort controller after the request is complete
        abortController = null
      })
  }

  const onPaymentRemindersChange = useCallback(
    (value: boolean) => {
      if (!value) {
        fetchUnsubscribe(
          { input: UnsubscribeGroupEnum.PaymentReminders },
          (results) => {
            if (results) {
              dispatch(setPaymentRemindersSubscription(false))
            }
          },
        )
      } else {
        fetchSubscribe(
          { input: UnsubscribeGroupEnum.PaymentReminders },
          (results) => {
            if (results) {
              dispatch(setPaymentRemindersSubscription(true))
            }
          },
        )
      }
    },
    [paymentRemindersSubscription],
  )

  const onPaymentUpdatesChange = useCallback(
    (value: boolean) => {
      if (!value) {
        fetchUnsubscribe(
          { input: UnsubscribeGroupEnum.PaymentUpdates },
          (results) => {
            if (results) {
              dispatch(setPaymentUpdatesSubscription(false))
            }
          },
        )
      } else {
        fetchSubscribe(
          { input: UnsubscribeGroupEnum.PaymentUpdates },
          (results) => {
            if (results) {
              dispatch(setPaymentUpdatesSubscription(true))
            }
          },
        )
      }
    },
    [paymentUpdatesSubscription],
  )

  useEffect(() => {
    fetchUnsubscribeGroups((results) => {
      if (results) {
        results.suppressions.forEach((suppression) => {
          if (suppression.id === UnsubscribeGroupEnum.PaymentReminders) {
            dispatch(setPaymentRemindersSubscription(!suppression.suppressed))
          } else if (suppression.id === UnsubscribeGroupEnum.PaymentUpdates) {
            dispatch(setPaymentUpdatesSubscription(!suppression.suppressed))
          }
        })
      }
    })
  }, [])

  return (
    <>
      <Box sx={styles.contactSection}>
        <Paper component="section" data-testid="Sec_Profile_ContactPreference">
          <SectionHeader
            overridableSxProps={sectionHeaderStyleOverride}
            title={t('UserProfile.PageContactInfoPrefsHeading')}
            testId="Hed_Profile_ContactPreference"
          />
          <Box sx={styles.modeButtonContainer}>
            {isViewOnlyMode ? (
              <Button
                sx={styles.button}
                data-testid="Btn_Pencil_EditForm"
                aria-label={t('UserProfile.ContactPreferenceForm.Edit')}
                onClick={pencilIconClicked}>
                <PenIcon />
              </Button>
            ) : (
              <Button
                sx={styles.button}
                data-testid="Btn_Close_CancelForm"
                aria-label={t('UserProfile.ContactPreferenceForm.Cancel')}
                onClick={closeIconClicked}>
                <CloseIcon />
              </Button>
            )}
          </Box>
          <Box sx={styles.contactPreferencesContainer}>
            {isViewOnlyMode ? (
              <ContactPreferenceView />
            ) : (
              <ContactPreferenceForm />
            )}
          </Box>
        </Paper>
        {isEmailUnsubscribeFeatureEnabled && (
          <EmailNotificationPreferences
            onPaymentRemindersChange={onPaymentRemindersChange}
            onPaymentUpdatesChange={onPaymentUpdatesChange}
          />
        )}
      </Box>
      <CustomErrorAlert
        testId="Banner_UserProfileEmailPreferences_SystemError"
        header="UserProfile.EmailPreferences.Alert"
        open={error}
        close={() => {
          setError(false)
          dispatch(setSubscriptionErrors([]))
        }}
        message="UserProfile.EmailPreferences.UnableToProcessRequestMessage"
      />
    </>
  )
}

export default PreferencesContainer
